import React, { useEffect, useMemo, useState } from "react";

function LooksFeedPage() {
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reportReason, setReportReason] = useState({});
  const [busyId, setBusyId] = useState(null);

  const apiBase = useMemo(() => process.env.REACT_APP_API_BASE_URL, []);

  const loadLooks = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetch(`${apiBase}/api/looks/public?limit=36`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || "Failed to load looks");
      }
      setLooks(data.looks || []);
    } catch (err) {
      setError(err.message || "Failed to load looks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportLook = async (lookId) => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      setError("Please log in to report an image.");
      return;
    }

    const reason = (reportReason[lookId] || "").trim();
    if (!reason) {
      setError("Please enter a reason before reporting.");
      return;
    }

    try {
      setBusyId(lookId);
      setError("");

      const res = await fetch(`${apiBase}/api/looks/${lookId}/report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || "Report failed");
      }

      // Remove it from visible feed immediately after report/hide.
      setLooks((prev) => prev.filter((l) => l._id !== lookId));
      setReportReason((prev) => ({ ...prev, [lookId]: "" }));
    } catch (err) {
      setError(err.message || "Report failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}>
      <h1 style={{ marginTop: 0 }}>Looks Feed</h1>
      <p style={{ color: "#6b7280" }}>
        Published looks. Report any inappropriate image and it will be hidden pending review.
      </p>

      {error && (
        <div
          style={{
            marginBottom: "12px",
            padding: "10px 12px",
            border: "1px solid #fecaca",
            borderRadius: "8px",
            background: "#fef2f2",
            color: "#991b1b",
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <p>Loading looks...</p>
      ) : looks.length === 0 ? (
        <p>No looks available.</p>
      ) : (
        <div
          style={{
            display: "grid",
            gap: "14px",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          }}
        >
          {looks.map((look) => (
            <div
              key={look._id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <img
                src={look.images?.[0]?.url || look.image?.url}
                alt={look.bookingSnapshot?.serviceName || "Look"}
                style={{ width: "100%", height: "220px", objectFit: "cover", background: "#f3f4f6" }}
              />
              <div style={{ padding: "10px" }}>
                <div style={{ fontWeight: 700, marginBottom: "4px" }}>
                  {look.bookingSnapshot?.serviceName || "Service"}
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>
                  {look.tags?.lookType || "look"}
                </div>
                <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "8px" }}>
                  {(look.viewCount || 0).toLocaleString()} views
                </div>
                <input
                  type="text"
                  value={reportReason[look._id] || ""}
                  onChange={(e) =>
                    setReportReason((prev) => ({ ...prev, [look._id]: e.target.value }))
                  }
                  placeholder="Reason to report"
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    padding: "8px",
                    fontSize: "12px",
                    marginBottom: "8px",
                  }}
                />

                <button
                  onClick={() => reportLook(look._id)}
                  disabled={busyId === look._id}
                  style={{
                    width: "100%",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    background: "#fff1f2",
                    color: "#9f1239",
                    padding: "8px",
                    fontWeight: 600,
                    cursor: busyId === look._id ? "not-allowed" : "pointer",
                  }}
                >
                  {busyId === look._id ? "Reporting..." : "Report image"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LooksFeedPage;
